import { addMonths, format, lastDayOfMonth, setDate } from 'date-fns';

/** Fallback shown wherever a date is absent or cannot be parsed. */
export const MISSING_DATE_PLACEHOLDER = '—';

const UK_DATE_PATTERN = /^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{4})$/;

/**
 * Parses a date value coming from an external finance API.
 *
 * Handles ISO strings, `dd/MM/yyyy` (and `dd-MM-yyyy` / `dd.MM.yyyy`) values,
 * epoch milliseconds and `Date` instances. Returns `null` when the value is
 * absent or cannot be interpreted, so callers never end up with an
 * `Invalid Date` (formatting one throws in date-fns).
 */
export function parseFlexibleDate(value: string | number | Date | null | undefined): Date | null {
  if (value == null) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'number') {
    const fromEpoch = new Date(value);
    return Number.isNaN(fromEpoch.getTime()) ? null : fromEpoch;
  }

  const trimmed = value.trim();
  if (!trimmed) return null;

  const ukMatch = UK_DATE_PATTERN.exec(trimmed);
  if (ukMatch) {
    const [, day, month, year] = ukMatch;
    const parsed = new Date(Number(year), Number(month) - 1, Number(day));
    if (
      Number.isNaN(parsed.getTime()) ||
      parsed.getFullYear() !== Number(year) ||
      parsed.getMonth() !== Number(month) - 1 ||
      parsed.getDate() !== Number(day)
    ) {
      return null;
    }
    return parsed;
  }

  const parsed = new Date(trimmed);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Formats a date value from an external finance API, returning `fallback`
 * instead of throwing when the value is absent or unparseable.
 */
export function formatDateSafe(
  value: string | number | Date | null | undefined,
  pattern: string,
  fallback: string = MISSING_DATE_PLACEHOLDER,
): string {
  const parsed = parseFlexibleDate(value);
  return parsed ? format(parsed, pattern) : fallback;
}

/**
 * Returns the epoch milliseconds for a date value, or `null` when it cannot be
 * parsed — for comparators that must not produce `NaN`.
 */
export function getTimeSafe(value: string | number | Date | null | undefined): number | null {
  const parsed = parseFlexibleDate(value);
  return parsed ? parsed.getTime() : null;
}

/**
 * Converts a date value to an ISO string, or `undefined` when it cannot be
 * parsed — used to normalise dates crossing the API boundary.
 */
export function toIsoDateStringSafe(value: string | number | Date | null | undefined): string | undefined {
  const parsed = parseFlexibleDate(value);
  return parsed ? parsed.toISOString() : undefined;
}

export type DueDateType =
  | 'days-from-invoice'
  | 'end-of-following-month'
  | 'day-of-following-month'
  | 'days-after-end-of-month';

/**
 * Maps the raw `dueType` string returned by the Financials GraphQL API
 * (enum DueDaysType) to the frontend DueDateType.
 *
 * GraphQL enum values: DaysFromInvoice, EndOfFollowingMonth,
 * DayOfFollowingMonth, DaysAfterEndOfMonth.
 */
export function mapApiDueType(apiValue: string | null | undefined): DueDateType {
  switch (apiValue) {
    case 'DaysFromInvoice':
    case 'Days':
      return 'days-from-invoice';
    case 'EndOfFollowingMonth':
      return 'end-of-following-month';
    case 'DayOfFollowingMonth':
      return 'day-of-following-month';
    case 'DaysAfterEndOfMonth':
      return 'days-after-end-of-month';
    default:
      return 'days-from-invoice';
  }
}

/**
 * Calculates the invoice due date based on the customer's payment terms.
 *
 * @param invoiceDate  The invoice date
 * @param dueType      How the due date should be computed
 * @param dueDays      Number of days (interpretation depends on dueType)
 */
export function calculateDueDate(
  invoiceDate: Date,
  dueType: DueDateType,
  dueDays: number,
): Date {
  switch (dueType) {
    case 'days-from-invoice':
      return new Date(invoiceDate.getTime() + dueDays * 24 * 60 * 60 * 1000);

    case 'end-of-following-month':
      return lastDayOfMonth(addMonths(invoiceDate, 1));

    case 'day-of-following-month': {
      const nextMonth = addMonths(invoiceDate, 1);
      const lastDay = lastDayOfMonth(nextMonth).getDate();
      const clampedDay = Math.min(dueDays, lastDay);
      return setDate(nextMonth, clampedDay);
    }

    case 'days-after-end-of-month': {
      const endOfMonth = lastDayOfMonth(invoiceDate);
      return new Date(endOfMonth.getTime() + dueDays * 24 * 60 * 60 * 1000);
    }

    default:
      return new Date(invoiceDate.getTime() + dueDays * 24 * 60 * 60 * 1000);
  }
}
